import {
  createContext, useContext, useState, useCallback,
  useEffect, type ReactNode,
} from 'react';
import { getSellerProducts, setSellerProducts } from '../api/seller.js';

export type ProductKey = 'cop_lop' | 'm8' | 'avira';

export interface ProductDef {
  key:      ProductKey;
  label:    string;
  subtitle: string;
  icon:     string;
  color:    string;
}

export const PRODUCTS: ProductDef[] = [
  {
    key:      'cop_lop',
    label:    'COP-LOP',
    subtitle: 'Lift controller — registration mandatory before install.',
    icon:     '🛗',
    color:    'blue',
  },
  {
    key:      'm8',
    label:    'M8 Light',
    subtitle: 'Ceiling & stair light controller — flexible registration timing.',
    icon:     '💡',
    color:    'teal',
  },
  {
    key:      'avira',
    label:    'Avira',
    subtitle: 'Third product controller.',
    icon:     '⚡',
    color:    'violet',
  },
];

interface ProductContextValue {
  selected:       ProductKey[];
  hasSelected:    boolean;
  isLoaded:       boolean;
  setSelected:    (products: ProductKey[]) => void;
  addProduct:     (key: ProductKey) => void;
  removeProduct:  (key: ProductKey) => void;
  clearSelection: () => void;
}

const ProductContext = createContext<ProductContextValue | null>(null);

function loadLocalSelected(): ProductKey[] {
  try {
    const raw = localStorage.getItem('seller_products');
    if (!raw) return [];
    return JSON.parse(raw) as ProductKey[];
  } catch {
    return [];
  }
}

function saveLocal(products: ProductKey[]) {
  localStorage.setItem('seller_products', JSON.stringify(products));
}

export function ProductProvider({ children }: { children: ReactNode }) {
  // Seed from localStorage immediately (no flash of empty), then reconcile with server.
  const [selected, setSelectedState] = useState<ProductKey[]>(loadLocalSelected);
  const [isLoaded, setIsLoaded] = useState(false);

  // On mount: load from server and replace localStorage cache.
  useEffect(() => {
    let cancelled = false;
    getSellerProducts()
      .then((res) => {
        if (!cancelled) {
          const products = res.selectedProducts as ProductKey[];
          setSelectedState(products);
          saveLocal(products);
          setIsLoaded(true);
        }
      })
      .catch(() => {
        // Fall back to localStorage on error (e.g. offline / not logged in yet).
        setIsLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const setSelected = useCallback((products: ProductKey[]) => {
    saveLocal(products);
    setSelectedState(products);
    // Fire-and-forget persist to server.
    setSellerProducts(products).catch(() => undefined);
  }, []);

  const addProduct = useCallback((key: ProductKey) => {
    setSelectedState((prev) => {
      const next = prev.includes(key) ? prev : [...prev, key];
      saveLocal(next);
      setSellerProducts(next).catch(() => undefined);
      return next;
    });
  }, []);

  const removeProduct = useCallback((key: ProductKey) => {
    setSelectedState((prev) => {
      const next = prev.filter((k) => k !== key);
      saveLocal(next);
      setSellerProducts(next).catch(() => undefined);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    localStorage.removeItem('seller_products');
    setSelectedState([]);
    setSellerProducts([]).catch(() => undefined);
  }, []);

  return (
    <ProductContext.Provider value={{
      selected,
      hasSelected: selected.length > 0,
      isLoaded,
      setSelected,
      addProduct,
      removeProduct,
      clearSelection,
    }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts(): ProductContextValue {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error('useProducts must be used inside ProductProvider');
  return ctx;
}
